import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './OrderConfirmation.css';
import { ORDER_STATUS } from '../../firebase/orders';
import { syncPaymongoPayment } from '../../firebase/orders';
import { db, functions } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

function formatDate(value) {
    try {
        if (!value) return '';
        if (value && typeof value.toDate === 'function') {
            value = value.toDate();
        } else if (value && typeof value === 'object' && value.seconds) {
            // Handle serialized Firestore Timestamps (passed via react-router state)
            value = new Date(value.seconds * 1000);
        }
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
        }
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return '';
    }
}

function OrderConfirmation() {
    const navigate       = useNavigate();
    const routerLocation = useLocation();

    const {
        orderId         = '',
        orderFirestoreId = '',
        orderItems      = [],
        grandTotal      = 0,
        paymentMethod   = 'PayMongo',
        newStatus       = '',
        estimatedDeliveryDate = '',
        checkoutUrl     = '',
    } = routerLocation.state || {};

    const [syncMessage, setSyncMessage] = useState('');
    const [syncLoading, setSyncLoading] = useState(false);
    const [resolvedStatus, setResolvedStatus] = useState(newStatus);
    const [fetchedTotal, setFetchedTotal] = useState(grandTotal);

    const awaitingPayment = resolvedStatus === ORDER_STATUS.AWAITING_PAYMENT && (fetchedTotal > 0 || grandTotal > 0);
    const statusLabel  = awaitingPayment ? 'Awaiting Payment' : 'In Production';
    const statusColor  = awaitingPayment ? '#7b1fa2' : '#2d7a3a';
    const statusBg     = awaitingPayment ? '#f3e5f5' : '#e6f4ea';

    const subtitleMsg  = awaitingPayment
        ? 'Complete payment on PayMongo (GCash, card, or e-wallet). If you already paid but this page still shows awaiting payment, tap Sync Payment below — do not pay twice.'
        : 'Your order has been placed! Our team will start production right away. You\'ll be notified when your order is ready for pickup or delivery.';

    // Fetch order details to ensure we have the correct total, in case state was lost or old cached JS was used
    useEffect(() => {
        if (!orderFirestoreId) return;
        const fetchOrder = async () => {
            try {
                const docRef = doc(db, 'orders', orderFirestoreId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const subtotal = data.total || 0;
                    const deliveryFee = data.deliveryFee || 0;
                    const discount = data.discountAmount || 0;
                    const calculatedTotal = Math.max(0, subtotal + deliveryFee - discount);
                    setFetchedTotal(data.orderTotal || calculatedTotal);
                    if (data.status) setResolvedStatus(data.status);
                }
            } catch (err) {
                console.error('Error fetching order details:', err);
            }
        };
        fetchOrder();
    }, [orderFirestoreId]);

    // Poor-connection recovery: poll PayMongo once when customer lands here after checkout.
    useEffect(() => {
        if (!awaitingPayment || !orderFirestoreId || !functions) return;
        let cancelled = false;
        (async () => {
            setSyncLoading(true);
            const { paid, error } = await syncPaymongoPayment(orderFirestoreId);
            if (cancelled) return;
            setSyncLoading(false);
            if (paid) {
                setResolvedStatus(ORDER_STATUS.IN_PRODUCTION);
                setSyncMessage('Payment confirmed. Your receipt has been recorded.');
            } else if (error) {
                setSyncMessage('');
            }
        })();
        return () => { cancelled = true; };
    }, [awaitingPayment, orderFirestoreId]);

    const handleManualSync = async () => {
        if (!orderFirestoreId) return;
        setSyncLoading(true);
        setSyncMessage('');
        const { paid, error, receiptNumber } = await syncPaymongoPayment(orderFirestoreId);
        setSyncLoading(false);
        if (error) {
            setSyncMessage('Could not sync payment yet. Please try again in a moment.');
            return;
        }
        if (paid) {
            setResolvedStatus(ORDER_STATUS.IN_PRODUCTION);
            setSyncMessage(receiptNumber ? `Payment confirmed. Receipt #${receiptNumber}` : 'Payment confirmed.');
        } else {
            setSyncMessage('PayMongo has not confirmed payment yet. Finish checkout or wait a moment, then try again.');
        }
    };

    return (
        <div className="order-confirmation-page">
            <div className="confirmation-modal-overlay">
                <div className="confirmation-modal-wrapper">
                    <button
                        className="modal-close-btn"
                        onClick={() => navigate('/')}
                        aria-label="Close"
                    >
                        ✕
                    </button>

                    <div className="confirmation-modal">
                        <div className="confirmation-content">

                            <div className="confirmation-hero">
                                <div className="confirmation-hero-inner">
                                    <div className="confirmation-check-circle">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                    </div>
                                    <h3>Order Placed!</h3>
                                    <p>{subtitleMsg}</p>
                                        <div className="confirmation-status-pill" style={{ background: statusBg, color: statusColor }}>
                                            {statusLabel}
                                        </div>
                                        {awaitingPayment && checkoutUrl && (
                                            <a
                                                href={checkoutUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="confirmation-btn"
                                                style={{ marginTop: '16px', display: 'block', textAlign: 'center', textDecoration: 'none' }}
                                            >
                                                Pay Now (Open PayMongo)
                                            </a>
                                        )}
                                        {awaitingPayment && orderFirestoreId && (
                                            <button
                                                type="button"
                                                className="confirmation-btn"
                                                style={{ marginTop: '12px' }}
                                                onClick={handleManualSync}
                                                disabled={syncLoading}
                                            >
                                                {syncLoading ? 'Syncing…' : 'Sync Payment'}
                                            </button>
                                    )}
                                    {syncMessage && <p style={{ marginTop: '8px', fontSize: '13px' }}>{syncMessage}</p>}
                                </div>
                            </div>

                            <div className="confirmation-details">
                                <h2 className="confirmation-title">Thank you for your Order!</h2>

                                <div className="confirmation-order-header">
                                    <span className="confirmation-order-label">Order</span>
                                    <span className="confirmation-order-id">#{orderId || '—'}</span>
                                </div>

                                {paymentMethod && (
                                    <div className="confirmation-payment-method">
                                        <span>Payment:</span>
                                        <strong>{paymentMethod}</strong>
                                    </div>
                                )}

                                {estimatedDeliveryDate && (
                                    <div className="confirmation-payment-method">
                                        <span>Est. delivery:</span>
                                        <strong>{formatDate(estimatedDeliveryDate)}</strong>
                                    </div>
                                )}

                                <div className="confirmation-items-list">
                                    {orderItems.map((item, idx) => (
                                        <div key={item.id || idx} className="confirmation-item">
                                            {item.image && <img src={item.image} alt={item.name} className="item-thumb" />}
                                            <div>
                                                <strong>Item #{idx + 1}</strong>
                                                <p>{item.type} · {item.quantity} pcs</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="confirmation-total">
                                    <span>Total</span>
                                    <strong>₱ {Number(fetchedTotal || 0).toLocaleString()}</strong>
                                </div>

                                <button className="confirmation-btn" onClick={() => navigate('/account/purchase-history')}>
                                    View Purchase History
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrderConfirmation;
